import { useEffect, useRef, useState } from "react";
import { Share2, Copy, Check, X, MessageCircle, Twitter, Facebook, Linkedin, Mail, Send, Link as LinkIcon } from "lucide-react";

// Native share sheet where supported (mobile Safari, Chrome Android, Edge desktop),
// otherwise a compact popover with Copy Link + one-tap deep links to the biggest channels.
export default function ShareProfileButton({ user, className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef(null);

  // The visible URL preview in the popover — clean, brandable, this is what recipients
  // see in their address bar AFTER the redirect.
  const cleanUrl = typeof window !== "undefined"
    ? `${window.location.origin}/u/${user.id}`
    : `/u/${user.id}`;
  // The URL actually shared — hits the backend endpoint that serves OG / Twitter Card meta
  // tags so link previews render a rich business card. Real browsers redirect instantly.
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/api/share/u/${user.id}`
    : `/api/share/u/${user.id}`;

  const firstName = (user.name || "Hooman").split(" ")[0];
  const profession = user.category || (user.categories || [])[0] || "Hooman";
  const title = `${user.name} — ${profession} on Hoomanz`;
  const text  = `Check out ${firstName} on Hoomanz — ${profession}${user.location ? ` in ${user.location.split(",")[0]}` : ""}. Book directly, no middleman.`;

  // Close popover on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openShare = async () => {
    // Prefer native share on capable browsers so mobile users get all their installed apps.
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return;
      } catch (e) {
        // User cancelled or Share failed — fall through to the popover.
        if (e?.name !== "AbortError") setOpen(true);
        return;
      }
    }
    setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Copy this link:", shareUrl);
    }
  };

  const enc = encodeURIComponent;
  const shareLinks = [
    { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, href: `https://wa.me/?text=${enc(text + " " + shareUrl)}` },
    { id: "telegram", label: "Telegram", Icon: Send,          href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(text)}` },
    { id: "x",        label: "X",        Icon: Twitter,       href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}` },
    { id: "facebook", label: "Facebook", Icon: Facebook,      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}` },
    { id: "linkedin", label: "LinkedIn", Icon: Linkedin,      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}` },
    { id: "email",    label: "Email",    Icon: Mail,          href: `mailto:?subject=${enc(title)}&body=${enc(text + "\n\n" + shareUrl)}` },
  ];

  return (
    <div className="relative" ref={wrapRef}>
      <button onClick={openShare} className={`btn-secondary ${className}`} data-testid="profile-share-btn"
        aria-haspopup="menu" aria-expanded={open}>
        <Share2 size={16}/> <span>Share</span>
      </button>

      {open && (
        <div role="menu" data-testid="profile-share-menu"
          className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-lg border border-[#E5E5E3] overflow-hidden z-40">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E3]">
            <p className="font-medium text-sm">Share {firstName}&apos;s profile</p>
            <button onClick={()=>setOpen(false)} aria-label="Close" className="text-[#8A8A8A] hover:text-[#0A0A0A]">
              <X size={14}/>
            </button>
          </div>

          <div className="p-3">
            <div className="flex items-center gap-2 bg-[#F2F2F0] rounded-full px-3 py-2">
              <LinkIcon size={13} className="text-[#8A8A8A] flex-shrink-0"/>
              <span className="text-xs text-[#4A4A4A] truncate flex-1" data-testid="profile-share-url">{cleanUrl}</span>
              <button onClick={copy}
                className={`text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${copied ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#0A0A0A] hover:text-white"}`}
                data-testid="profile-share-copy">
                {copied ? <><Check size={11}/> Copied</> : <><Copy size={11}/> Copy</>}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1 px-3 pb-3">
            {shareLinks.map(({ id, label, Icon, href }) => (
              <a key={id} href={href} target="_blank" rel="noreferrer"
                onClick={()=>setOpen(false)}
                data-testid={`profile-share-${id}`}
                className="flex flex-col items-center gap-1 py-3 rounded-xl hover:bg-[#F2F2F0] text-[#0A0A0A] transition-colors">
                <Icon size={18}/>
                <span className="text-[11px]">{label}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
